'use client'

import { Wallet, Hand, LogIn, Sparkles, Star, BarChart2, Camera, Globe, Tag, UserPlus } from 'lucide-react'
import { useAuth } from '@/components/auth/auth-provider'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'

export default function HomePage() {
  const { user, loading } = useAuth()
  const router = useRouter()
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const [activeSlide, setActiveSlide] = useState(0)

  useEffect(() => {
    if (!loading && user) {
      router.push('/dashboard')
    }
  }, [user, loading, router])

  // Si hay un usuario autenticado, no mostrar la página de inicio
  if (!loading && user) {
    return null
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>

      </div>
    )
  }

  if (user) {
    return null // Will redirect to dashboard
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-between p-4 sm:p-6">
      {/* Hero Section */}
      <section className="text-center py-4 sm:py-8 px-2 sm:px-4 max-w-4xl mx-auto flex-grow flex flex-col justify-center w-full">
        <div className="mx-auto mb-2 sm:mb-4 w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-header flex items-center justify-center">
          <Wallet className="h-12 w-12 sm:h-14 sm:w-14 text-header-foreground" />
        </div>
        <h1 className="font-display text-3xl sm:text-5xl font-extrabold mb-2 sm:mb-4 leading-tight px-2">
          Bienvenido a <span className="text-primary">PocketWise</span>
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground mb-4 sm:mb-6 max-w-2xl mx-auto px-2">
          Gestiona tus finanzas de manera inteligente y sencilla.
          Controla tus gastos e ingresos con una interfaz intuitiva y reportes claros.
        </p>
        {/* Compact trial note */}
        <div className="text-xs sm:text-sm text-accent mb-2 sm:mb-3 font-medium">
          7 días de Pro gratis al registrarte
        </div>
        <div className="flex flex-col sm:flex-row justify-center gap-3 w-full max-w-xs sm:max-w-md mx-auto">
          <Link href="/auth/login" className="w-full">
            <Button
              className="w-full h-11 sm:h-12 px-4 text-sm sm:text-base rounded-full bg-header hover:bg-header/90 text-header-foreground shadow-lg transition-all duration-300 transform hover:scale-[1.02] active:scale-95"
              size="lg"
            >
              <LogIn className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              <span>Iniciar Sesión</span>
            </Button>
          </Link>
          <Link href="/auth/register" className="w-full">
            <Button
              variant="outline"
              className="w-full h-11 sm:h-12 px-4 text-sm sm:text-base rounded-full border-2 border-header text-foreground hover:bg-header/10 shadow-lg transition-all duration-300 transform hover:scale-[1.02] active:scale-95"
              size="lg"
            >
              <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              <span>Crear Cuenta</span>
            </Button>
          </Link>
        </div>
        {/* Dots for mobile carousel */}
        <div className="flex sm:hidden items-center justify-center gap-2 mt-2">
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className={`h-2 w-2 rounded-full ${activeSlide === i ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-6 px-2 sm:px-4 max-w-5xl mx-auto w-full">
        <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-center mb-4 sm:mb-6 text-foreground flex items-center justify-center gap-2">
          <Star className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-primary" />
          <span>Características Principales</span>
        </h2>
        <div
          ref={carouselRef}
          onScroll={() => {
            const el = carouselRef.current
            if (!el) return
            const idx = Math.round(el.scrollLeft / (el.clientWidth * 0.85 + 12))
            setActiveSlide(Math.max(0, Math.min(2, idx)))
          }}
          className="flex overflow-x-auto snap-x snap-mandatory gap-3 px-2 pb-2 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 sm:overflow-visible"
        >
          <div className="min-w-[85%] sm:min-w-0 flex flex-col items-center text-center p-4 sm:p-5 bg-card rounded-2xl shadow-lg border border-border transition-all duration-300 hover:shadow-xl hover:border-primary/50 snap-center">
            <div className="p-2 mb-2 sm:mb-3 bg-primary/10 rounded-xl">
              <BarChart2 className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
            </div>
            <h3 className="text-sm sm:text-base font-semibold mb-1 sm:mb-2 text-foreground">Reportes Detallados</h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">Visualiza tus finanzas con gráficas claras, análisis completos y descarga tus reportes en CSV.</p>
          </div>

          <div className="min-w-[85%] sm:min-w-0 flex flex-col items-center text-center p-4 sm:p-5 bg-card rounded-2xl shadow-lg border border-border transition-all duration-300 hover:shadow-xl hover:border-primary/50 snap-center">
            <div className="p-2 mb-2 sm:mb-3 bg-primary/10 rounded-xl">
              <Camera className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
            </div>
            <h3 className="text-sm sm:text-base font-semibold mb-1 sm:mb-2 text-foreground">Escaneo de Tickets</h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">Registra gastos al instante con nuestra IA de escaneo.</p>
          </div>

          <div className="min-w-[85%] sm:min-w-0 flex flex-col items-center text-center p-4 sm:p-5 bg-card rounded-2xl shadow-lg border border-border transition-all duration-300 hover:shadow-xl hover:border-primary/50 snap-center">
            <div className="p-2 mb-2 sm:mb-3 bg-primary/10 rounded-xl">
              <Globe className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
            </div>
            <h3 className="text-sm sm:text-base font-semibold mb-1 sm:mb-2 text-foreground">Múltiples Monedas</h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">Soporte para diversas monedas de todo el mundo.</p>
          </div>
        </div>
      </section>

      {/* Plans & Pricing */}
      <section className="py-6 px-2 sm:px-4 max-w-6xl mx-auto w-full">
        <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-center mb-4 sm:mb-6 text-foreground">Planes y Precios</h2>
        <p className="text-center text-sm sm:text-base text-muted-foreground mb-5">
          Crea tu cuenta y obtén <span className="text-primary font-medium">7 días</span> de nuestra versión <span className="text-accent font-medium">Pro gratis</span>.
          Al finalizar, puedes elegir <span className="font-medium text-foreground">Plus</span>, <span className="font-medium text-foreground">Pro</span> o continuar con <span className="font-medium text-foreground">Free</span> (se activa automáticamente).
        </p>

        {/* Mobile carousel */}
        <div className="flex md:hidden overflow-x-auto snap-x snap-mandatory gap-3 px-2 pb-2">
          {/* Free */}
          <div className="min-w-[85%] snap-center rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-lg sm:text-xl font-semibold text-muted-foreground">Free</h3>
              <span className="text-xs px-2 py-1 rounded-full bg-muted border border-border text-muted-foreground">Gratis</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Perfecto para empezar sin compromiso.</p>
            <div className="space-y-1 mb-4">
              <p className="text-2xl font-bold">$0 <span className="text-sm font-medium text-muted-foreground">/ siempre</span></p>
              <p className="text-xs text-accent">Se activa automáticamente tras el trial</p>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 mb-5 list-disc list-inside">
              <li>50 transacciones/mes</li>
              <li>10 categorías personalizadas</li>
              <li>Gráficos básicos</li>
            </ul>
            <div className="flex gap-2">
              <Link href="/auth/register" className="w-full">
                <Button variant="outline" className="w-full rounded-full">Crear Cuenta</Button>
              </Link>
            </div>
          </div>

          {/* Plus */}
          <div className="min-w-[85%] snap-center rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-lg sm:text-xl font-semibold text-primary">Plus</h3>
              <span className="text-xs px-2 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary">Popular</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Para quien quiere controlar sus gastos con reportes claros.</p>
            <div className="space-y-1 mb-4">
              <p className="text-2xl font-bold">$21.900 <span className="text-sm font-medium text-muted-foreground">/ mes</span></p>
              <p className="text-sm text-muted-foreground">$240.900 / año <span className="text-primary">(1 mes gratis)</span></p>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 mb-5 list-disc list-inside">
              <li>Transacciones ilimitadas</li>
              <li>Reportes y CSV</li>
              <li>Pagos recurrentes mensuales</li>
            </ul>
            <div className="flex gap-2">
              <Link href="/auth/register" className="w-full">
                <Button className="w-full rounded-full bg-header hover:bg-header/90 text-header-foreground">Crear Cuenta</Button>
              </Link>
            </div>
          </div>

          {/* Pro */}
          <div className="min-w-[85%] snap-center rounded-2xl border border-accent/40 bg-card p-4 sm:p-6 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-lg sm:text-xl font-semibold text-accent">Pro</h3>
              <span className="text-xs px-2 py-1 rounded-full bg-accent/10 border border-accent/30 text-accent">Recomendado</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Para usuarios exigentes que quieren el máximo detalle.</p>
            <div className="space-y-1 mb-4">
              <p className="text-2xl font-bold">$31.900 <span className="text-sm font-medium text-muted-foreground">/ mes</span></p>
              <p className="text-sm text-muted-foreground">$350.900 / año <span className="text-accent">(1 mes gratis)</span></p>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 mb-5 list-disc list-inside">
              <li>Todo lo de Plus</li>
              <li>Análisis avanzados</li>
              <li>Soporte prioritario</li>
              <li>Escaneo de tickets con IA</li>
              <li>Coach Financiero IA</li>
            </ul>
            <div className="flex gap-2">
              <Link href="/auth/register" className="w-full">
                <Button className="w-full rounded-full bg-accent hover:bg-accent/90 text-white">Comenzar prueba Pro</Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop grid */}
        <div className="hidden md:grid grid-cols-3 gap-4 sm:gap-6">
          {/* Free */}
          <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-lg sm:text-xl font-semibold text-muted-foreground">Free</h3>
              <span className="text-xs px-2 py-1 rounded-full bg-muted border border-border text-muted-foreground">Gratis</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Perfecto para empezar sin compromiso.</p>
            <div className="space-y-1 mb-4">
              <p className="text-2xl font-bold">$0 <span className="text-sm font-medium text-muted-foreground">/ siempre</span></p>
              <p className="text-xs text-accent">Se activa automáticamente tras el trial</p>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 mb-5 list-disc list-inside">
              <li>50 transacciones/mes</li>
              <li>10 categorías personalizadas</li>
              <li>Gráficos básicos</li>
            </ul>
            <div className="flex gap-2">
              <Link href="/auth/register" className="w-full">
                <Button variant="outline" className="w-full rounded-full">Crear Cuenta</Button>
              </Link>
            </div>
          </div>

          {/* Plus */}
          <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-lg sm:text-xl font-semibold text-primary">Plus</h3>
              <span className="text-xs px-2 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary">Popular</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Para quien quiere controlar sus gastos con reportes claros.</p>
            <div className="space-y-1 mb-4">
              <p className="text-2xl font-bold">$21.900 <span className="text-sm font-medium text-muted-foreground">/ mes</span></p>
              <p className="text-sm text-muted-foreground">$240.900 / año <span className="text-primary">(1 mes gratis)</span></p>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 mb-5 list-disc list-inside">
              <li>Transacciones ilimitadas</li>
              <li>Reportes y CSV</li>
              <li>Pagos recurrentes mensuales</li>
            </ul>
            <div className="flex gap-2">
              <Link href="/auth/register" className="w-full">
                <Button className="w-full rounded-full bg-header hover:bg-header/90 text-header-foreground">Crear Cuenta</Button>
              </Link>
            </div>
          </div>

          {/* Pro */}
          <div className="rounded-2xl border border-accent/40 bg-card p-4 sm:p-6 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-lg sm:text-xl font-semibold text-accent">Pro</h3>
              <span className="text-xs px-2 py-1 rounded-full bg-accent/10 border border-accent/30 text-accent">Recomendado</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Para usuarios exigentes que quieren el máximo detalle.</p>
            <div className="space-y-1 mb-4">
              <p className="text-2xl font-bold">$31.900 <span className="text-sm font-medium text-muted-foreground">/ mes</span></p>
              <p className="text-sm text-muted-foreground">$350.900 / año <span className="text-accent">(1 mes gratis)</span></p>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 mb-5 list-disc list-inside">
              <li>Todo lo de Plus</li>
              <li>Análisis avanzados</li>
              <li>Soporte prioritario</li>
              <li>Escaneo de tickets con IA</li>
              <li>Coach Financiero IA</li>
            </ul>
            <div className="flex gap-2">
              <Link href="/auth/register" className="w-full">
                <Button className="w-full rounded-full bg-accent hover:bg-accent/90 text-white">Comenzar prueba Pro</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full py-3 sm:py-4 text-center text-muted-foreground text-xs sm:text-sm border-t border-border mt-4 sm:mt-6">
        <div className="max-w-4xl mx-auto px-4">
          <p>© {new Date().getFullYear()} PocketWise. Todos los derechos reservados.</p>
          <p className="mt-1 text-muted-foreground/70 text-xs">Versión 1.0.0</p>
        </div>
      </footer>
    </div>
  )
}
